import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { isFastly } from "@faire/web/common/images/optimizeImages";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useMemo } from "react";
import styled from "styled-components";

import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";
import { filterInvalidImages } from "@faire/retailer/lib/filterInvalidImages";

import { IProductImageProps, ProductImage } from "../ProductImage";

interface ITileImageProps {
  image?: IImage;
  showVerticalImagery?: boolean;
  imageDimension?: IProductImageProps["imageDimension"];
  children?: React.ReactNode | undefined;
  imageContainerRef?: React.RefObject<HTMLDivElement>;
  isEvaluate?: boolean;
  imageAltText?: string;
  productToken?: string;
  useLazyImageLoading?: boolean;
  className?: string;
  imageBreakpoints?: string;
}

export const TileImage: React.FC<
  ITileImageProps & React.HTMLAttributes<HTMLDivElement>
> = ({
  image,
  showVerticalImagery,
  imageDimension,
  children,
  imageContainerRef,
  imageAltText,
  productToken,
  useLazyImageLoading,
  imageBreakpoints,
  className,
  ...rest
}) => {
  const {
    isPending: validateImageLoading,
    isError: validateImageError,
    data: validateImageData,
  } = useQuery({
    queryKey: ["validate-image-result", image, image?.url],
    queryFn: async () => {
      if (image?.url && !isFastly(image.url)) {
        return await filterInvalidImages([image]);
      } else {
        return [];
      }
    },
  });

  const primaryImage = useMemo(() => {
    if (image?.url && isFastly(image.url)) {
      return image;
    } else if (
      validateImageLoading ||
      validateImageError ||
      !validateImageData
    ) {
      return PLACEHOLDER_IMAGE;
    } else {
      return validateImageData[0] ?? PLACEHOLDER_IMAGE;
    }
  }, [validateImageError, validateImageLoading, validateImageData, image]);

  const altText = primaryImage?.alt_text ?? imageAltText;
  //Set the image dimension to match productImage -> COMMON_TILE_IMAGE_DIMENSION
  return (
    <ImageContainer
      key={primaryImage.token}
      ref={imageContainerRef}
      data-test-id={`carousel-image-${primaryImage.token}`}
      className={className}
      {...rest}
    >
      <ProductImage
        key={primaryImage.token}
        image={primaryImage}
        imageDimension={
          imageDimension ?? {
            mobileAndAbove: 370,
            tabletAndAbove: 350,
            xLargeAndAbove: 300,
          }
        }
        showVerticalImagery={showVerticalImagery}
        alt={altText ?? ""}
        useLazyImageLoading={useLazyImageLoading}
        imageBreakpoints={imageBreakpoints}
        shouldIncrementallyLoadImages
      />
      {children}
    </ImageContainer>
  );
};

const ImageContainer = styled.div`
  position: relative;
`;

const PLACEHOLDER_IMAGE = IImage.build({
  token: "p_placeholder",
  url: EAEAEA_PIXEL,
});
